import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { useApi } from '../lib/api'
import { useAuth } from '@clerk/react'
import { RichEditor } from '../components/RichEditor'
import { Spinner } from '../components/Spinner'
import type { Newsletter } from '../lib/types'

interface DraftFields {
  slug: string
  title: string
  category: 'weekly' | 'monthly' | 'one-off'
  body_md: string
  cover_url: string | null
  published_at: string | null
}

const empty: DraftFields = {
  slug: '',
  title: '',
  category: 'one-off',
  body_md: '',
  cover_url: null,
  published_at: null,
}

export function NewsletterEdit() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { call } = useApi()
  const { getToken } = useAuth()
  const [draft, setDraft] = useState<DraftFields>(empty)
  const [loading, setLoading] = useState<boolean>(Boolean(id))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  // Load existing newsletter
  useEffect(() => {
    if (!id) return
    let cancelled = false
    setLoading(true)
    call<Newsletter>(`/newsletters/${id}`)
      .then((n) => {
        if (cancelled) return
        setDraft({
          slug: n.slug,
          title: n.title,
          category: n.category,
          body_md: n.body_md,
          cover_url: n.cover_url,
          published_at: n.published_at,
        })
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [id, call])

  // Auto-derive slug from title for new newsletters.
  useEffect(() => {
    if (id) return
    setDraft((d) => {
      if (d.slug) return d
      return { ...d, slug: slugify(d.title) }
    })
  }, [draft.title, id])

  async function handleSave({ publishNow }: { publishNow: boolean }) {
    setError(null)
    setSaving(true)
    try {
      const payload = {
        slug: draft.slug,
        title: draft.title,
        category: draft.category,
        body_md: draft.body_md,
        cover_url: draft.cover_url,
        published_at: publishNow
          ? new Date().toISOString()
          : draft.published_at,
      }
      if (id) {
        await call<Newsletter>(`/newsletters/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        })
        navigate('/newsletters')
      } else {
        const created = await call<Newsletter>('/newsletters', {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        navigate(`/newsletters/${created.id}`)
      }
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function handleCoverUpload(file: File) {
    setError(null)
    setUploading(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch('/api/newsletter-images', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'content-type': file.type,
        },
        body: file,
      })
      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status} ${await res.text()}`)
      }
      const json = (await res.json()) as { url: string }
      setDraft((d) => ({ ...d, cover_url: json.url }))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setUploading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <header>
        <Link to="/newsletters" className="text-sm text-rust-600 hover:underline">
          ← Newsletters
        </Link>
        <h1 className="mt-2 font-serif text-3xl font-semibold text-marrow-900 dark:text-bone-50">
          {id ? 'Edit newsletter' : 'New newsletter'}
        </h1>
      </header>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Title">
          <input
            type="text"
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            className="input"
            placeholder="Why Your LDL Might Rise on Keto"
          />
        </Field>
        <Field label="Slug">
          <input
            type="text"
            value={draft.slug}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
            className="input font-mono text-sm"
            placeholder="ldl-on-keto"
          />
        </Field>
        <Field label="Category">
          <select
            value={draft.category}
            onChange={(e) =>
              setDraft({ ...draft, category: e.target.value as DraftFields['category'] })
            }
            className="input"
          >
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="one-off">One-off</option>
          </select>
        </Field>
        <Field label="Schedule">
          <input
            type="datetime-local"
            value={
              draft.published_at
                ? new Date(draft.published_at).toISOString().slice(0, 16)
                : ''
            }
            onChange={(e) =>
              setDraft({
                ...draft,
                published_at: e.target.value
                  ? new Date(e.target.value).toISOString()
                  : null,
              })
            }
            className="input"
          />
        </Field>
      </div>

      <Field label="Cover image">
        <div className="flex flex-wrap items-center gap-3">
          {draft.cover_url ? (
            <div className="relative">
              <img
                src={draft.cover_url}
                alt=""
                className="h-24 w-40 rounded-md object-cover"
              />
              <button
                type="button"
                onClick={() => setDraft({ ...draft, cover_url: null })}
                className="absolute right-1 top-1 rounded bg-marrow-900/80 px-1.5 py-0.5 text-xs text-bone-50 hover:bg-marrow-900"
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex h-24 w-40 items-center justify-center rounded-md border border-dashed border-bone-300 text-xs text-marrow-800/60 dark:border-marrow-700 dark:text-bone-200/60">
              No cover
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleCoverUpload(f)
            }}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="rounded-md border border-bone-300 bg-white px-3 py-2 text-sm font-medium text-marrow-900 transition hover:bg-bone-100 disabled:opacity-50 dark:border-marrow-700 dark:bg-marrow-800 dark:text-bone-50 dark:hover:bg-marrow-700"
          >
            {uploading ? 'Uploading…' : draft.cover_url ? 'Replace' : 'Upload cover'}
          </button>
        </div>
      </Field>

      <Field label="Body">
        <RichEditor
          value={draft.body_md}
          onChange={(md) => setDraft({ ...draft, body_md: md })}
          placeholder="Start writing the newsletter body in markdown…"
        />
      </Field>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-bone-200 pt-4 dark:border-marrow-700">
        <button
          type="button"
          disabled={saving}
          onClick={() => handleSave({ publishNow: false })}
          className="rounded-md border border-bone-300 bg-white px-4 py-2 text-sm font-medium text-marrow-900 hover:bg-bone-100 disabled:opacity-50 dark:border-marrow-700 dark:bg-marrow-800 dark:text-bone-50 dark:hover:bg-marrow-700"
        >
          Save draft
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => handleSave({ publishNow: true })}
          className="rounded-md bg-rust-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-rust-600 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Publish now'}
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wider text-marrow-800/60 dark:text-bone-200/60">
        {label}
      </span>
      {children}
    </label>
  )
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80)
}
